<template>
  <ModalBase show-modal auto-h extra-narrow @close-modal="close" class="communication-scheduler-modal">
    <template v-slot:titlebar>
      <TitleBar variant-class="title-modal title-modal--simple">
        <template v-slot:title>
          {{ $gettext('Select date and time') }}
        </template>
      </TitleBar>
    </template>

    <template v-slot:maincontent>
      <b-form novalidate class="scheduler-container">
        <div class="scheduler-inputs">
          <div class="scheduler-inputs--calendar">
            <b-calendar
              v-model="form.scheduled_at_date"
              id="compose-scheduled-at-date"
              hide-header
              :show-year-nav="false"
              :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }"
              :value-as-date="true"
              weekday-header-format="narrow"
              @context="onCalendarCtx"
            >
              <!-- Custom current month/year label -->
              <template #nav-this-month>
                <div class="w-100 text-left pl-2">
                  {{ monthYearLabel }}
                </div>
              </template>
            </b-calendar>
          </div>
          <div class="scheduler-inputs--time">
            <div role="group">
              <b-form-input id="date-input" :value="formattedDate" readonly />
            </div>
            <div role="group">
              <b-form-input
                id="time-input"
                v-model.trim="form.scheduled_at_time"
                placeholder="e.g. 14:30"
                :state="timeState"
                @input="validateTime"
                @blur="normalizeTime"
                maxlength="5"
                inputmode="numeric"
                pattern="^(?:[01]\d|2[0-3]):[0-5]\d$"
              />
              <b-form-invalid-feedback id="time-input-feedback">
                {{ $gettext('Invalid time') }}
              </b-form-invalid-feedback>
            </div>
          </div>
        </div>
        <div style="width: 100%; text-align: right">
          <GekkoButton :text="$gettext('Cancel')" :disabled="sending" @click="close()" variant="link" />
          <GekkoButton
            :text="(isEdit) ? $gettext('Save') : $gettext('Schedule send')"
            :loading="sending"
            @click="scheduleSend"
            class="position-relative"
          />
        </div>
      </b-form>
    </template>
  </ModalBase>
</template>

<script>
import ModalBase from '@/components/base_components/ModalBase.vue'
import TitleBar from '@/components/TitleBar'
import GekkoButton from '@/components/GekkoButton'

export default {
  name: 'CommunicationSchedulerModal',
  components: {
    ModalBase,
    TitleBar,
    GekkoButton,
  },
  props: {
    presetDate: {
      type: Date,
      default: () => new Date()
    },
  },
  data() {
    return {
      form: {
        scheduled_at_date: new Date(),
        scheduled_at_time: new Date().toTimeString().slice(0, 5),
      },
      errors: {},
      timeState: null,
      calendarCtx: null,
      sending: false,
    }
  },
  computed: {
    monthYearLabel() {
      // Use the calendar's active month/year
      const d = this.calendarCtx?.activeDate || new Date()
      const locale = this.calendarCtx?.calendarLocale || undefined
      return d.toLocaleString(locale, { month: 'long', year: 'numeric' })
    },
    formattedDate() {
      const d = this.form.scheduled_at_date
      if (!d) return '' // handles when you call clearDate()
      const date = d instanceof Date ? d : new Date(d)
      if (isNaN(date)) return ''
      // Use the calendar locale if available; en-GB gives "1 Nov 2025"
      const locale = this.calendarCtx?.calendarLocale || 'en-GB'
      return date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    },
    isEdit () {
      return (this.$route.params['communication_id'])
    }
  },
  created () {
    this.setupData();
  },
  methods: {
    close() {
      this.$emit('close', false)
    },
    setupData () {
      if (this.presetDate) {
        this.form.scheduled_at_date = new Date(
          this.presetDate.getFullYear(),
          this.presetDate.getMonth(),
          this.presetDate.getDate()
        )
        this.form.scheduled_at_time = this.presetDate.toTimeString().slice(0, 5)
      }
    },
    onCalendarCtx(c) {
      this.calendarCtx = c
    }, // fires on create and whenever the view changes
    validateTime() {
      let v = String(this.form.scheduled_at_time || '').trim()
      if (v.length > 5) v = v.slice(0, 5) // still OK if they paste extra
      this.form.scheduled_at_time = v

      // Allow H:mm or HH:mm (00–23 : 00–59)
      const isValid = /^(?:[01]?\d|2[0-3]):[0-5]\d$/.test(v)
      this.timeState = v === '' ? null : isValid
    },

    // Optional: normalize to HH:mm after they're done typing
    normalizeTime() {
      const m = /^(\d{1,2}):([0-5]\d)$/.exec(this.form.scheduled_at_time || '')
      if (!m) return
      let [, hh, mm] = m
      const h = Number(hh)
      if (h >= 0 && h <= 23) {
        this.form.scheduled_at_time = String(h).padStart(2, '0') + ':' + mm // "9:32" -> "09:32"
        this.timeState = true
      }
    },
    setToday() {
      const now = new Date()
      this.form.scheduled_at_date = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    },
    clearDate() {
      this.form.scheduled_at_date = ''
    },
    scheduleSend() {
      this.sending = true
      const date = new Date(this.form.scheduled_at_date)
      const [hh, mm] = (this.form.scheduled_at_time || '00:00').split(':').map(Number)
      date.setHours(hh || 0, mm || 0, 0, 0)
      this.$emit('schedule-send', date)
    },
  },
}
</script>

<style lang="scss">
.communication-scheduler-modal {
  .gk-modal {
    max-width: 516px !important;
    border-radius: 28px;
    .gk-modal-content {
      .close-btn {
        display: none;
      }
    }
  }
  .titlebar.title-modal {
    padding-left: 14px;
    padding-top: 10px;
  }
  .scheduler-container {
    display: flex;
    flex-direction: column;
    // gap: 1rem;
    padding: 0.25rem;
    padding-top: 0.75rem;
    padding-bottom: 0;
  }
  .scheduler-inputs {
    display: flex;
    gap: 1rem;
    justify-content: space-between;
    &--calendar {
      .b-calendar {
        width: 224px;
        .b-calendar-inner {
          width: 224px;
        }
        .b-calendar-grid-body,
        .b-calendar-grid-weekdays,
        .b-calendar-nav {
          width: 224px;
        }
        .b-calendar-grid-body {
          height: 200px;
          .col[data-date] .btn {
            margin: 0 auto;
          }
        }
        .b-calendar-grid-weekdays {
          border: none !important;
        }
        .b-calendar-grid-caption {
          display: none !important;
        }
        .b-calendar-grid-help {
          display: none;
        }
        .b-calendar-header,
        .b-calendar-nav {
          margin-bottom: 0.75rem;
        }
        .b-calendar-nav {
          button {
            text-align: right;
            flex-grow: 0 !important;
            &:hover {
              background: white;
              color: #222;
            }
            &[aria-label*='year'] {
              display: none !important;
            }
            &[aria-label*='Previous'] {
              text-align: left;
              flex-grow: 0 !important;
              order: 2;
              width: 2rem;
            }
            &[aria-label*='Next'] {
              text-align: right;
              flex-grow: 0 !important;
              order: 3;
              width: 2rem;
            }
            &[aria-label*='Current'] {
              pointer-events: none;
              cursor: initial;
              flex-grow: 1 !important;
              font-weight: normal !important;
              font-size: 1rem;
              order: 0;
              color: rgb(32, 33, 36);
            }
          }
        }
      }
      .form-control {
        background: white;
        border-radius: 0;
        box-shadow: none;
        color: $colorTextBase;
      }
    }
    &--time {
      display: flex;
      flex-direction: column;
      gap: 2rem;
      flex: 1;
      max-width: 200px;
      .form-control {
        background: white;
        box-shadow: none;
        color: $colorTextBase;
        border: 1px solid #747875;
        padding: 1rem;
        height: 56px;
        font-size: 18px;
        border-radius: $radiusMd;
        &#time-input:focus {
          border-color: $colorBrand;
        }
      }
    }
  }
}
</style>